import type { ImportBatch } from '@servicetim/shared';
import { Button, buttonVariants, EmptyState } from '@servicetim/ui/components';
import { Link } from '@tanstack/react-router';

export interface RunTabProps {
  batch: ImportBatch;
  onCancel: () => void;
  cancelPending: boolean;
}

const CANCELLABLE: ReadonlySet<ImportBatch['status']> = new Set([
  'discovering',
  'mapping',
  'dry_run',
  'running',
]);

/** S-09-05's run progress: `stats` from `import_batch`, updated per chunk; done → a queue link. */
export function RunTab({ batch, onCancel, cancelPending }: RunTabProps) {
  const { stats } = batch;
  if (stats.chunksCompleted === 0 && batch.status !== 'running' && batch.status !== 'cancelled') {
    return <EmptyState title="Not run yet" description="Run the import to see progress here." />;
  }

  const total = batch.discovery?.ticketCount ?? null;
  const percent = total ? Math.min(100, Math.round((stats.recordsSeen / total) * 100)) : null;
  const done =
    batch.status === 'completed' || batch.status === 'failed' || batch.status === 'cancelled';

  return (
    <div className="space-y-6">
      <section aria-label="Progress" className="space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="font-medium">Progress</span>
          <span className="text-fg-muted">
            {stats.recordsSeen}
            {total !== null ? ` / ${total}` : ''}
            {percent !== null ? ` (${percent}%)` : ''} · {stats.chunksCompleted} chunk
            {stats.chunksCompleted === 1 ? '' : 's'}
          </span>
        </div>
        <progress
          aria-label="Import progress"
          value={stats.recordsSeen}
          max={Math.max(total ?? stats.recordsSeen, 1)}
          className="h-2 w-full overflow-hidden rounded-full [&::-webkit-progress-bar]:bg-surface-sunken [&::-webkit-progress-value]:bg-brand-600"
        />
      </section>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
        <Stat label="Tickets created" value={stats.ticketsCreated} />
        <Stat label="Tickets updated" value={stats.ticketsUpdated} />
        <Stat label="Comments" value={stats.commentsCreated} />
        <Stat label="Attachments copied" value={stats.attachmentsCopied} />
        <Stat label="Attachments skipped" value={stats.attachmentsSkipped} />
        <Stat label="Users created" value={stats.usersCreated} />
        <Stat label="Statuses created" value={stats.statusesCreated} />
        <Stat label="Fields created" value={stats.fieldsCreated} />
      </dl>

      {stats.errors > 0 ? (
        <p role="alert" className="text-red-700 dark:text-red-300 text-sm">
          {stats.errors} record{stats.errors === 1 ? '' : 's'} failed — see the batch error and
          worker logs for detail.
        </p>
      ) : null}

      {batch.error ? (
        <p role="alert" className="text-red-700 dark:text-red-300 text-sm">
          {batch.error}
        </p>
      ) : null}

      {!done ? (
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={cancelPending || !CANCELLABLE.has(batch.status)}
        >
          Cancel
        </Button>
      ) : null}

      {batch.status === 'completed' && batch.projectId ? (
        <Link
          to="/queue"
          search={{ project: batch.projectId }}
          className={buttonVariants({ variant: 'default' })}
        >
          Open the project's queue
        </Link>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-fg-muted">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
